// src/index.js
import React from 'react';
import ReactDOM from 'react-dom/client'; // Or 'react-dom' for older React
import './index.css'; // Your global styles
import App from './App'; // Your main App component
import reportWebVitals from './reportWebVitals';

import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext'; // Keep AuthProvider high up
import { TemplateProvider } from './contexts/TemplateContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { TourProvider } from './contexts/TourContext';
import { TRAK } from './theme/trakTokens';
import StagingRibbon from './components/StagingRibbon';

// MUI theme. The 2026 redesign (design handoff "TrakLog UI.dc.html") moves the
// app off the Material default look: IBM Plex Sans for UI and IBM Plex Mono for
// ids/timestamps/sizes, hairline borders instead of elevation, flatter radii,
// and the tasks page on a #f6f8f6 ground. The per-screen detail lives in `sx`;
// only what should hold everywhere is set here.
const theme = createTheme({
  palette: {
    primary: {
      main: TRAK.green, // TrakLog green (WCAG AA compliant: 5.41:1 on white)
      dark: TRAK.greenDark,
    },
    secondary: {
      main: '#dc004e', // Example secondary color
    },
    error: { main: TRAK.danger },
    warning: { main: TRAK.warn },
    text: {
      primary: TRAK.text,
      secondary: TRAK.muted,
    },
    divider: TRAK.hairline,
    background: {
      default: TRAK.page,
    },
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: TRAK.sans,
    // IDs, timestamps, byte sizes and counts are scanned, not read — the mono
    // numerals are what make a column of them comparable at a glance.
    mono: { fontFamily: TRAK.mono },
    button: { textTransform: 'none', fontWeight: 600 },
    h5: { fontWeight: 600, letterSpacing: '-.3px' },
    h6: { fontWeight: 600 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 7 },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 9,
          border: `1px solid rgba(13,51,32,.12)`,
          boxShadow: TRAK.shadowMenu,
          padding: 6,
        },
        list: { padding: 0 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontSize: 12.5,
          fontWeight: 500,
          '&:hover': { backgroundColor: '#f4fbf6' },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: TRAK.ink, fontSize: 11, fontWeight: 500 },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}> {/* MUI ThemeProvider wraps everything */}
      <CssBaseline /> {/* MUI's baseline CSS reset/normalization */}
      <AuthProvider> {/* AuthProvider wraps App */}
        <OnboardingProvider> {/* OnboardingProvider for new user tutorial */}
          <TourProvider> {/* Shepherd.js guided tour for new users */}
            <TemplateProvider> {/* TemplateProvider provides cached templates */}
              <App />
            </TemplateProvider>
          </TourProvider>
        </OnboardingProvider>
      </AuthProvider>
      <StagingRibbon /> {/* "STAGING" corner band; renders nothing on production */}
    </ThemeProvider>
  </React.StrictMode>
);

reportWebVitals();